import { createClient } from '@/lib/supabase/client';
import type { Purchase, PurchaseStatus } from '@/lib/database';
import { emptyToNull, newId, orThrow, todayStr, DEFAULT_PAGE_SIZE } from './useEntityList';

// DB column mapping (this file owns it):
//   po_status         <-> status
//   purchase_price_usd <-> cost_usd
//   order_date        <-> purchase_date
//   sku               <-> diamond_sku
// The purchases table has no `quantity` column; the UI value is always 1.
type PurchaseRow = {
  id: string;
  po_number: string;
  diamond_id: string | null;
  supplier_id: string | null;
  supplier_name: string;
  sku: string | null;
  shape: string | null;
  carat: number | null;
  color: string | null;
  clarity: string | null;
  purchase_price_usd: number;
  po_status: PurchaseStatus;
  order_date: string | null;
  expected_date: string | null;
  received_date: string | null;
  notes: string | null;
  created_at: string;
};

function toPurchase(row: PurchaseRow): Purchase {
  return {
    id: row.id,
    po_number: row.po_number,
    supplier_id: row.supplier_id ?? '',
    supplier_name: row.supplier_name,
    diamond_id: row.diamond_id ?? '',
    diamond_sku: row.sku ?? '',
    quantity: 1,
    cost_usd: Number(row.purchase_price_usd) || 0,
    status: row.po_status,
    purchase_date: row.order_date ?? '',
    expected_date: row.expected_date ?? '',
    received_date: row.received_date,
    notes: row.notes ?? '',
    created_at: row.created_at,
  };
}

function fromPurchase(
  input: Omit<Purchase, 'id' | 'created_at' | 'quantity'> & { received_date?: string | null }
): Record<string, unknown> {
  return emptyToNull(
    {
      po_number: input.po_number,
      supplier_id: input.supplier_id,
      supplier_name: input.supplier_name,
      diamond_id: input.diamond_id,
      sku: input.diamond_sku,
      purchase_price_usd: input.cost_usd,
      po_status: input.status,
      order_date: input.purchase_date || null,
      expected_date: input.expected_date || null,
      received_date: input.received_date || null,
      notes: input.notes ?? '',
    },
    ['supplier_id', 'diamond_id', 'sku']
  );
}

export async function listPurchases(): Promise<Purchase[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('purchases')
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .order('order_date', { ascending: false });
  return (orThrow(data, error) as PurchaseRow[]).map(toPurchase);
}

export async function createPurchase(
  input: Omit<Purchase, 'id' | 'created_at' | 'quantity'> & { received_date?: string | null }
): Promise<Purchase> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('purchases')
    .insert({ ...fromPurchase(input), id: newId('po'), created_at: new Date().toISOString() })
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  return toPurchase(orThrow(data, error) as PurchaseRow);
}

const COLUMN_BY_FIELD: Record<string, string> = {
  po_number: 'po_number',
  supplier_id: 'supplier_id',
  supplier_name: 'supplier_name',
  diamond_id: 'diamond_id',
  diamond_sku: 'sku',
  cost_usd: 'purchase_price_usd',
  status: 'po_status',
  purchase_date: 'order_date',
  expected_date: 'expected_date',
  received_date: 'received_date',
  notes: 'notes',
};

export async function updatePurchase(
  id: string,
  patch: Partial<Omit<Purchase, 'id' | 'created_at' | 'quantity'>>
): Promise<Purchase> {
  const supabase = createClient();
  const payload: Record<string, unknown> = {};
  for (const [field, column] of Object.entries(COLUMN_BY_FIELD)) {
    if (field in patch) {
      const value = (patch as Record<string, unknown>)[field];
      payload[column] =
        value === '' &&
        (field === 'supplier_id' ||
          field === 'diamond_id' ||
          field === 'diamond_sku' ||
          field === 'received_date')
          ? null
          : value;
    }
  }
  const { data, error } = await supabase
    .from('purchases')
    .update(payload)
    .eq('id', id)
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  return toPurchase(orThrow(data, error) as PurchaseRow);
}

/** Mark a PO received with today's date. */
export async function receivePurchase(id: string): Promise<Purchase> {
  return updatePurchase(id, { status: 'RECEIVED', received_date: todayStr() });
}
